import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Command } from 'cmdk';
import { useQuery } from '@tanstack/react-query';
import { Building2, Car, FileText, Search, Tags } from 'lucide-react';
import { api, qs } from '@/lib/api';
import { NAVIGATION } from './navigation';
import { useAuth } from '@/auth/AuthProvider';

/**
 * Command palette (Cmd/Ctrl-K).
 *
 * Combines page navigation with a debounced server-side search across
 * vehicles, properties, products and posts. Search is only issued for terms
 * of two characters or more, and the backend caps the result count.
 */

interface SearchHit {
  kind: 'vehicle' | 'realEstate' | 'product' | 'post';
  id: string;
  title: string;
  subtitle: string | null;
  href: string;
}

const KIND_ICONS = {
  vehicle: Car,
  realEstate: Building2,
  product: Tags,
  post: FileText,
} as const;

const KIND_LABELS = {
  vehicle: 'Vehicles',
  realEstate: 'Real estate',
  product: 'Products',
  post: 'Posts',
} as const;

export function CommandPalette({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const navigate = useNavigate();
  const { can } = useAuth();
  const [term, setTerm] = useState('');
  const [debounced, setDebounced] = useState('');

  // Debounce so typing does not fire a request per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(term.trim()), 200);
    return () => clearTimeout(timer);
  }, [term]);

  useEffect(() => {
    if (!open) setTerm('');
  }, [open]);

  const { data, isFetching } = useQuery({
    queryKey: ['search', debounced],
    queryFn: () => api.get<{ hits: SearchHit[] }>(`/admin/search${qs({ q: debounced })}`),
    enabled: open && debounced.length >= 2,
    staleTime: 15_000,
  });

  const pages = useMemo(
    () =>
      NAVIGATION.flatMap((group) =>
        group.items
          .filter((item) => !item.permission || can(item.permission))
          .map((item) => ({ label: item.label, to: item.to, group: group.label ?? 'Navigate' })),
      ),
    [can],
  );

  const grouped = useMemo(() => {
    const out = new Map<SearchHit['kind'], SearchHit[]>();
    for (const hit of data?.hits ?? []) {
      const list = out.get(hit.kind) ?? [];
      list.push(hit);
      out.set(hit.kind, list);
    }
    return [...out.entries()];
  }, [data]);

  const go = (to: string) => {
    onOpenChange(false);
    navigate(to);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center px-4 pt-[12vh]">
      <button
        type="button"
        aria-label="Close search"
        className="fixed inset-0 bg-black/25"
        onClick={() => onOpenChange(false)}
      />
      <Command
        label="Global search"
        className="relative w-full max-w-[34rem] overflow-hidden rounded-[var(--radius-lg)] border border-[var(--color-border-strong)] bg-[var(--color-canvas)] shadow-[0_16px_48px_rgba(0,0,0,0.16)]"
        loop
      >
        <div className="flex items-center gap-2 border-b border-[var(--color-border)] px-3">
          <Search className="h-3.5 w-3.5 shrink-0 text-[var(--color-text-subtle)]" />
          <Command.Input
            value={term}
            onValueChange={setTerm}
            placeholder="Search listings, products, posts, or jump to a page…"
            className="h-10 w-full bg-transparent text-[13px] text-[var(--color-text)] outline-none placeholder:text-[var(--color-text-subtle)]"
          />
          <kbd className="shrink-0 rounded border border-[var(--color-border)] px-1 text-[10px] text-[var(--color-text-subtle)]">
            ESC
          </kbd>
        </div>

        <Command.List className="max-h-[22rem] overflow-y-auto p-1.5">
          <Command.Empty className="px-2 py-6 text-center text-[12.5px] text-[var(--color-text-muted)]">
            {debounced.length >= 2
              ? isFetching
                ? 'Searching…'
                : 'No matches found'
              : 'Type at least two characters to search'}
          </Command.Empty>

          <Command.Group
            heading="Navigate"
            className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1 [&_[cmdk-group-heading]]:text-[10.5px] [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider [&_[cmdk-group-heading]]:text-[var(--color-text-subtle)]"
          >
            {pages.map((page) => (
              <Command.Item
                key={page.to}
                value={`${page.group} ${page.label}`}
                onSelect={() => go(page.to)}
                className="flex cursor-pointer items-center gap-2 rounded-[var(--radius-sm)] px-2 py-1.5 text-[13px] text-[var(--color-text)] data-[selected=true]:bg-[var(--color-hover)]"
              >
                <span className="text-[var(--color-text-subtle)]">{page.group}</span>
                <span className="text-[var(--color-text-subtle)]">/</span>
                <span>{page.label}</span>
              </Command.Item>
            ))}
          </Command.Group>

          {grouped.map(([kind, hits]) => {
            const Icon = KIND_ICONS[kind];
            return (
              <Command.Group
                key={kind}
                heading={KIND_LABELS[kind]}
                className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1 [&_[cmdk-group-heading]]:text-[10.5px] [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider [&_[cmdk-group-heading]]:text-[var(--color-text-subtle)]"
              >
                {hits.map((hit) => (
                  <Command.Item
                    key={hit.id}
                    value={`${hit.title} ${hit.subtitle ?? ''} ${hit.id}`}
                    onSelect={() => go(hit.href)}
                    className="flex cursor-pointer items-center gap-2 rounded-[var(--radius-sm)] px-2 py-1.5 text-[13px] data-[selected=true]:bg-[var(--color-hover)]"
                  >
                    <Icon className="h-3.5 w-3.5 shrink-0 text-[var(--color-text-subtle)]" />
                    <span className="min-w-0 flex-1 truncate text-[var(--color-text)]">{hit.title}</span>
                    {hit.subtitle ? (
                      <span className="shrink-0 text-[11.5px] text-[var(--color-text-subtle)]">
                        {hit.subtitle}
                      </span>
                    ) : null}
                  </Command.Item>
                ))}
              </Command.Group>
            );
          })}
        </Command.List>
      </Command>
    </div>
  );
}

/** Registers the Cmd/Ctrl-K shortcut. */
export function useCommandPalette() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'k' && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        setOpen((current) => !current);
      }
      if (event.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  return { open, setOpen };
}
